import { mediaUrl } from './mediaUrl';

/**
 * Банер заходу — дві форми залежно від джерела (ADR-0012).
 *
 * e-phan (з 2026 року) віддає окреме зображення на кожну орієнтацію:
 *   banner: {
 *     horizontal: { "640": url, "1280": url, "1920": url } | { default: url } | null,
 *     vertical:   { "640": url, ... } | { default: url } | null,
 *   }
 * Ключі — пікселі ширини для responsive-нарізки; "default" — банер, залитий
 * до появи нарізки. Кожна сторона окремо може бути не завантажена (null).
 *
 * Свій захід чи доповнення з CMS тримає одне зображення Strapi-медіа
 * ({ url, ... }) на обидві орієнтації — окремих вертикального й
 * горизонтального файлів там нема.
 *
 * normalizeBanner зводить обидві форми до одного вигляду, resolveBannerImage
 * дістає з нього src/srcSet для конкретного місця показу.
 */
function normalizeBanner(banner) {
  if (!banner) return { horizontal: null, vertical: null };

  if (banner.horizontal !== undefined || banner.vertical !== undefined) {
    return { horizontal: banner.horizontal || null, vertical: banner.vertical || null };
  }

  if (banner.url) {
    const side = { default: banner.url };
    return { horizontal: side, vertical: side };
  }

  return { horizontal: null, vertical: null };
}

/**
 * @param {object|null} banner event.banner як він є в записі
 * @param {'horizontal'|'vertical'} orientation яку сторону показати тут
 * @returns {{ src: string, srcSet: string|undefined }|null}
 */
export function resolveBannerImage(banner, orientation) {
  const normalized = normalizeBanner(banner);
  const other = orientation === 'horizontal' ? 'vertical' : 'horizontal';
  // Якщо потрібної орієнтації не завантажили, краще показати те, що є,
  // ніж силует-заглушку — це все ще фото цього заходу, лише не в тому кадрі.
  const side = normalized[orientation] || normalized[other];
  if (!side) return null;

  const sized = Object.entries(side)
    .filter(([key, url]) => key !== 'default' && url)
    .map(([key, url]) => [Number(key), url])
    .filter(([width]) => Number.isFinite(width))
    .sort((a, b) => a[0] - b[0]);

  if (sized.length === 0) {
    const url = side.default;
    return url ? { src: mediaUrl(url), srcSet: undefined } : null;
  }

  const srcSet = sized.map(([width, url]) => `${mediaUrl(url)} ${width}w`).join(', ');
  // src — запасний варіант для контексту без підтримки srcset: середній розмір,
  // не найменший (розмитий) і не найбільший (важкий) з можливих.
  const [, fallbackUrl] = sized[Math.floor((sized.length - 1) / 2)];

  return { src: mediaUrl(fallbackUrl), srcSet };
}
